import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  MapPin, Phone, ChevronRight, Shield, Users, Star, 
  CheckCircle, Award, Clock, Sparkles, ArrowRight 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { DoctorCard } from '../components/common/DoctorCard';
import { LogoLoader } from '../components/common/LogoLoader';
import { doctorsAPI, locationsAPI, marketingPagesAPI } from '../lib/api';
import { getWhatsAppLink } from '../lib/utils';
import { Seo, tryRedirect, assetUrl, absUrl } from '../lib/seo';
import { RichContent, InternalLinks } from '../components/common/RichContent';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

export const SEOLandingPage = () => {
  const { slug } = useParams();
  const [pageData, setPageData] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // First try to get from marketing pages
        const pageRes = await marketingPagesAPI.getBySlug(slug);
        setPageData(pageRes.data);
        
        // Get doctors for this city if it's a city page
        if (pageRes.data.city) {
          const locationsRes = await locationsAPI.getAll();
          const location = locationsRes.data.find(l => 
            l.city.toLowerCase() === pageRes.data.city.toLowerCase() ||
            l.slug === pageRes.data.city.toLowerCase().replace(' ', '-')
          );
          
          if (location) {
            const doctorsRes = await doctorsAPI.getAll({ 
              doctor_status: 'approved', 
              location_id: location.id,
              limit: 6 
            });
            setDoctors(doctorsRes.data);
          }
        }
      } catch (error) {
        if (error.response?.status === 404 && (await tryRedirect(`/landing/${slug}`, navigate))) return;
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFCF8]">
        <LogoLoader className="min-h-[70vh]" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center">
        <Seo title="Page not found | DrPortals" override={{ robots_index: false }} />
        <div className="text-center">
          <h2 className="font-accent text-2xl font-semibold text-[#23265d] mb-4">Page not found</h2>
          <Link to="/">
            <Button className="bg-[#23265d] hover:bg-[#1e1e4b]">Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const isMarketingPage = pageData.page_type === 'marketing-service';

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid={`seo-page-${slug}`}>
      <Seo
        page={pageData}
        title={pageData.meta_title || pageData.title}
        description={pageData.meta_description || pageData.intro_text}
        image={pageData.hero_image}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": isMarketingPage ? "Service" : "MedicalBusiness",
            "name": pageData.title,
            "description": pageData.meta_description,
            "url": absUrl(`/landing/${pageData.slug}`),
            ...(pageData.hero_image && { "image": absUrl(pageData.hero_image) }),
            ...(pageData.city && { "areaServed": pageData.city, "address": { "@type": "PostalAddress", "addressLocality": pageData.city } })
          },
          ...(pageData.faq?.length ? [{
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": pageData.faq.filter((f) => f.question && f.answer).map((f) => ({
              "@type": "Question", "name": f.question, "acceptedAnswer": { "@type": "Answer", "text": f.answer }
            }))
          }] : []),
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": absUrl('/') },
              { "@type": "ListItem", "position": 2, "name": pageData.h1_heading, "item": absUrl(`/landing/${pageData.slug}`) }
            ]
          }
        ]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#23265d] via-[#1e1e4b] to-[#23265d] text-white py-20 md:py-28 px-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#c5a065] rounded-full blur-[100px]" />
          <div className="absolute bottom-20 left-20 w-72 h-72 bg-[#5c65ff] rounded-full blur-[80px]" />
        </div>
        
        <motion.div 
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="relative z-10 max-w-7xl mx-auto"
        >
          {pageData.city && (
            <div className="flex items-center gap-2 text-[#c5a065] mb-4">
              <MapPin className="w-5 h-5" />
              <span className="font-medium">{pageData.city}</span>
            </div>
          )}
          
          <h1 className="font-accent text-4xl md:text-5xl lg:text-6xl font-semibold mb-6 tracking-tight">
            {pageData.h1_heading}
          </h1>
          
          <p className="text-lg md:text-xl text-white/70 mb-8 max-w-3xl leading-relaxed">
            {pageData.intro_text}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <a href={getWhatsAppLink(`Hi, I'm looking for ${isMarketingPage ? 'marketing services' : 'a dermatologist'} ${pageData.city ? `in ${pageData.city}` : ''}.`)} target="_blank" rel="noopener noreferrer">
              <Button className="rounded-full bg-[#25D366] hover:bg-[#25D366]/90 text-white px-8 py-6 gap-2 shadow-[0_0_20px_rgba(37,211,102,0.4)]" data-testid="hero-whatsapp-cta">
                <Phone className="w-5 h-5" />
                {isMarketingPage ? 'Get Free Consultation' : 'Find a Doctor Now'}
              </Button>
            </a>
            {!isMarketingPage && (
              <Link to="/doctors">
                <Button variant="outline" className="rounded-full border-white/30 text-white hover:bg-white hover:text-[#23265d] px-8 py-6">
                  Browse All Doctors
                </Button>
              </Link>
            )}
          </div>
        </motion.div>
      </section>

      {/* Trust Signals */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {isMarketingPage ? (
              <>
                <span className="flex items-center gap-2 text-gray-600">
                  <Award className="w-5 h-5 text-[#c5a065]" />
                  Industry Experts
                </span>
                <span className="flex items-center gap-2 text-gray-600">
                  <CheckCircle className="w-5 h-5 text-[#c5a065]" />
                  Proven Results
                </span>
                <span className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-5 h-5 text-[#c5a065]" />
                  Fast Turnaround
                </span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-2 text-gray-600">
                  <Shield className="w-5 h-5 text-[#c5a065]" />
                  Verified Doctors
                </span>
                <span className="flex items-center gap-2 text-gray-600">
                  <Users className="w-5 h-5 text-[#c5a065]" />
                  {doctors.length}+ Specialists
                </span>
                <span className="flex items-center gap-2 text-gray-600">
                  <Star className="w-5 h-5 text-[#c5a065]" />
                  Top Rated
                </span>
              </>
            )}
          </div>
        </div>
      </section>

      {(pageData.hero_image || pageData.content_html || pageData.internal_links?.length > 0) && (
        <section className="py-16 md:py-20 px-4 bg-white" data-testid="landing-rich-content">
          <div className="max-w-4xl mx-auto">
            {pageData.hero_image && (
              <img src={assetUrl(pageData.hero_image)} alt={pageData.hero_image_alt || pageData.h1_heading} loading="lazy"
                className="w-full rounded-2xl mb-10 object-cover max-h-[440px]" />
            )}
            <RichContent html={pageData.content_html} />
            <InternalLinks links={pageData.internal_links} />
          </div>
        </section>
      )}

      {/* Content Sections */}
      {pageData.content_sections && pageData.content_sections.length > 0 && (
        <section className="py-16 md:py-24 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pageData.content_sections.map((section, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl p-8 shadow-soft hover:shadow-hover transition-shadow"
                >
                  <div className="w-12 h-12 bg-[#23265d]/10 rounded-xl flex items-center justify-center mb-5">
                    <Sparkles className="w-6 h-6 text-[#c5a065]" />
                  </div>
                  <h3 className="font-accent text-xl font-semibold text-[#23265d] mb-4">
                    {section.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {section.content}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Doctors Grid (only for city pages) */}
      {!isMarketingPage && doctors.length > 0 && (
        <section className="py-16 md:py-24 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
              <div>
                <span className="text-[#c5a065] text-sm font-semibold tracking-[0.2em] uppercase">
                  Our Experts
                </span>
                <h2 className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d] mt-2">
                  Top Dermatologists {pageData.city && `in ${pageData.city}`}
                </h2>
              </div>
              <Link to="/doctors">
                <Button variant="outline" className="rounded-full border-[#23265d] text-[#23265d] hover:bg-[#23265d] hover:text-white group">
                  View All
                  <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {doctors.map((doctor) => (
                <DoctorCard key={doctor.id} doctor={doctor} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {pageData.faq && pageData.faq.length > 0 && (
        <section className="py-16 md:py-24 px-4 bg-[#FDFCF8]">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-[#c5a065] text-sm font-semibold tracking-[0.2em] uppercase">
                FAQ
              </span>
              <h2 className="font-accent text-3xl md:text-4xl font-semibold text-[#23265d] mt-4">
                Frequently Asked Questions
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="w-full">
              {pageData.faq.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`} 
                  className="bg-white rounded-xl mb-3 border border-gray-100 px-6"
                >
                  <AccordionTrigger className="text-left font-medium text-[#23265d] hover:no-underline py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 md:py-28 px-4 bg-gradient-to-br from-[#23265d] via-[#1e1e4b] to-[#23265d] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#c5a065]/10 rounded-full blur-[120px]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="font-accent text-3xl md:text-4xl lg:text-5xl font-semibold mb-6 tracking-tight">
            {isMarketingPage 
              ? 'Ready to Grow Your Practice?' 
              : `Find the Best Dermatologist ${pageData.city ? `in ${pageData.city}` : ''}`
            }
          </h2>
          <p className="text-lg text-white/70 mb-10 max-w-xl mx-auto">
            {isMarketingPage 
              ? 'Get a free consultation and learn how we can help you attract more patients.'
              : 'Our team will help you connect with verified specialists based on your specific needs.'
            }
          </p>
          <a href={getWhatsAppLink(`Hi, I need help ${isMarketingPage ? 'with marketing my dermatology practice' : 'finding a dermatologist'} ${pageData.city ? `in ${pageData.city}` : ''}.`)} target="_blank" rel="noopener noreferrer">
            <Button className="rounded-full bg-[#25D366] hover:bg-[#25D366]/90 text-white px-10 py-7 text-lg gap-3 shadow-[0_0_30px_rgba(37,211,102,0.4)]" data-testid="cta-whatsapp">
              <Phone className="w-5 h-5" />
              {isMarketingPage ? 'Get Free Consultation' : 'Chat on WhatsApp'}
            </Button>
          </a>
        </div>
      </section>

    </div>
  );
};

export default SEOLandingPage;
